/**
 * TB Mukt Bharat - Home Page
 * Fast & lightweight government portal landing page
 */

import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import GovLayout, { GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { useAuthStore } from '@/stores/authStore'
import { ArrowRight, BookOpen, Users, Video, Bell, Shield, Heart, TrendingUp, Globe, MessageCircle, Award, Home } from 'lucide-react'

// ================================================================================
// STAT CARD COMPONENT
// ================================================================================
const StatCard = React.memo(({ icon: Icon, value, label }: { icon: React.ComponentType<{ className?: string }>; value: string; label: string }) => (
  <div className="text-center p-4">
    <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-50 flex items-center justify-center">
      <Icon className="w-6 h-6 text-blue-600" />
    </div>
    <p className="text-2xl md:text-3xl font-bold text-gray-900">{value}</p>
    <p className="text-sm text-gray-500">{label}</p>
  </div>
))

// ================================================================================
// FEATURE CARD COMPONENT
// ================================================================================
const FeatureCard = React.memo(({ icon: Icon, title, description, color }: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  color: string
}) => (
  <GovCard className="h-full">
    <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center mb-4`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-600">{description}</p>
  </GovCard>
))

// ================================================================================
// STEP CARD COMPONENT
// ================================================================================
const StepCard = React.memo(({ step, title, description }: { step: string; title: string; description: string }) => (
  <div className="text-center p-4">
    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white text-xl font-bold shadow-lg">
      {step}
    </div>
    <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
))

// ================================================================================
// FLOATING BADGE COMPONENT
// ================================================================================
const FloatingBadge = React.memo(({ icon: Icon, value, label, className }: {
  icon: React.ComponentType<{ className?: string }>;
  value: string;
  label: string;
  className: string
}) => (
  <div className={`bg-white rounded-xl shadow-lg p-3 flex items-center gap-2 ${className}`}>
    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500">
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-lg font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  </div>
))

// ================================================================================
// MAIN HOME PAGE
// ================================================================================
const HomePage = () => {
  const { isAuthenticated } = useAuthStore()

  const features = useMemo(() => [
    { icon: BookOpen, title: 'Knowledge Center', description: 'Access comprehensive TB information, prevention tips, and treatment guides.', color: 'bg-gradient-to-br from-emerald-500 to-teal-500' },
    { icon: Users, title: 'Community Support', description: 'Connect with healthcare professionals and TB survivors from across India.', color: 'bg-gradient-to-br from-blue-500 to-cyan-500' },
    { icon: Video, title: 'Educational Videos', description: 'Watch informative reels and stories about TB awareness and recovery journeys.', color: 'bg-gradient-to-br from-purple-500 to-pink-500' },
    { icon: Bell, title: 'Real-time Updates', description: 'Stay informed with latest news, health campaigns, and government initiatives.', color: 'bg-gradient-to-br from-orange-500 to-red-500' },
    { icon: Shield, title: 'Privacy Protected', description: 'Your health data is secured with enterprise-grade encryption and privacy controls.', color: 'bg-gradient-to-br from-green-500 to-emerald-500' },
    { icon: Heart, title: 'Support Network', description: 'Join support groups, share experiences, and help others in their TB journey.', color: 'bg-gradient-to-br from-rose-500 to-pink-500' },
  ], [])

  const stats = useMemo(() => [
    { icon: Users, value: '1M+', label: 'Active Users' },
    { icon: BookOpen, value: '500+', label: 'Articles' },
    { icon: Globe, value: '100+', label: 'Healthcare Partners' },
    { icon: Award, value: '28+', label: 'States Covered' },
  ], [])

  const steps = useMemo(() => [
    { step: '01', title: 'Create Account', description: 'Sign up for free and complete your profile' },
    { step: '02', title: 'Connect & Learn', description: 'Access knowledge resources and join communities' },
    { step: '03', title: 'Share & Support', description: 'Share your story and support others in their journey' },
  ], [])

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-8">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-cyan-50 -z-10" />

          <GovCard className="relative">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left Content */}
              <div>
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium mb-4">
                  <Home className="w-4 h-4" />
                  India's #1 TB Awareness Platform
                </div>

                <GovPageTitle
                  title="Together, We Can Make India TB-Free"
                  subtitle="Join millions of Indians in the fight against tuberculosis. Learn, share, and support each other on this journey towards a healthier India."
                />

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  {isAuthenticated ? (
                    <Link to="/feed">
                      <GovButton size="lg" className="w-full sm:w-auto">
                        Go to Feed
                        <ArrowRight className="w-4 h-4 ml-2 inline" />
                      </GovButton>
                    </Link>
                  ) : (
                    <>
                      <Link to="/register">
                        <GovButton size="lg" className="w-full sm:w-auto">
                          Join Now - It's Free
                          <ArrowRight className="w-4 h-4 ml-2 inline" />
                        </GovButton>
                      </Link>
                      <Link to="/login">
                        <GovButton variant="secondary" size="lg" className="w-full sm:w-auto">
                          Sign In
                        </GovButton>
                      </Link>
                    </>
                  )}
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap items-center gap-4 mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Shield className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">100% Secure</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">1M+ Users</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <Globe className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">Pan-India</span>
                  </div>
                </div>
              </div>

              {/* Right Content - Image with Badges */}
              <div className="relative hidden md:block">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl" />
                <img
                  src="/buddha-peace.svg"
                  alt="TB-Free India"
                  className="w-full max-w-sm mx-auto drop-shadow-xl"
                  loading="lazy"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />

                {/* Floating Badges */}
                <FloatingBadge
                  icon={TrendingUp}
                  value="98%"
                  label="Recovery Rate"
                  className="absolute -bottom-2 left-0"
                />
                <FloatingBadge
                  icon={MessageCircle}
                  value="24/7"
                  label="Support"
                  className="absolute -top-2 right-0"
                />
              </div>
            </div>
          </GovCard>
        </section>

        {/* Stats Section */}
        <section>
          <GovCard>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {stats.map((stat, index) => (
                <StatCard key={index} {...stat} />
              ))}
            </div>
          </GovCard>
        </section>

        {/* Features Section */}
        <section>
          <div className="text-center mb-6">
            <GovPageTitle
              title="Why Join TB Mukt Bharat?"
              subtitle="Our platform provides everything you need to learn, connect, and contribute to the fight against tuberculosis."
              icon={Shield}
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} />
            ))}
          </div>
        </section>

        {/* How It Works Section */}
        <section>
          <GovCard>
            <div className="text-center mb-6">
              <GovPageTitle
                title="How It Works"
                subtitle="Get started in three simple steps"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {steps.map((item, index) => (
                <StepCard key={index} {...item} />
              ))}
            </div>
          </GovCard>
        </section>

        {/* CTA Section */}
        <section>
          <div className="bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl p-6 md:p-8 text-white text-center shadow-lg">
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Ready to Make a Difference?
            </h2>
            <p className="text-white/90 mb-6 max-w-xl mx-auto">
              Join our community today and be part of the movement to eliminate TB from India.
            </p>
            <Link to={isAuthenticated ? '/feed' : '/register'}>
              <button className="px-6 py-3 bg-white hover:bg-gray-100 text-blue-600 font-semibold rounded-lg shadow-md transition-colors inline-flex items-center gap-2">
                {isAuthenticated ? 'Explore Feed' : 'Get Started'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center py-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            &copy; 2024 TB Mukt Bharat. All rights reserved. Made with care for a healthier India.
          </p>
        </footer>
      </div>
    </GovLayout>
  )
}

export default React.memo(HomePage)